import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { Clipboard } from '@angular/cdk/clipboard';
import { AppInput } from '../app-input/app-input';
import { AppDropdownComponent, DropdownOption } from '../app-dropdown/app-dropdown.component';
import { AppTooltipComponent } from '../app-tooltip/app-tooltip.component';
import { ToasterService } from '../toaster/toaster.service';

import {
  EditableFieldStatus,
  EditableOverrideReasonOption,
  EditableTableColumn,
  EditableTableStatusLabels,
  EditableTableValueChange,
  EditableReviewRow,
} from './editable-review-table.model';

@Component({
  selector: 'app-editable-review-table',
  standalone: true,
  imports: [AppDropdownComponent, AppInput, AppTooltipComponent, CommonModule, FormsModule, MatIcon],
  templateUrl: './editable-review-table.component.html',
  styleUrl: './editable-review-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditableReviewTable {
  @Input() hintText = '';
  @Input() showHint = false;
  @Input() rows: EditableReviewRow[] = [];
  @Input() columns: EditableTableColumn[] = [];
  @Input() overrideReasonOptions: EditableOverrideReasonOption[] = [];
  @Input() emptyStateText = 'No rows available.';
  @Input() clickToEditTooltipText = 'Click to edit';
  @Input() editedBadgeText = 'edited';
  @Input() isReadOnly = false;
  @Input() invalidOverrideReasonRowKeys: string[] = [];
  @Input() invalidFinalValueRowKeys: string[] = [];
  @Input() enableCopyMode = false;
  @Input() statusLabels: EditableTableStatusLabels = {
    match: 'Match',
    mismatch: 'Mismatch',
    corrected: 'Corrected',
    accepted: 'Accepted',
  };

  @Output() finalValueChanged = new EventEmitter<{ rowKey: string; value: string }>();
  @Output() overrideReasonChanged = new EventEmitter<{ rowKey: string; reason: string }>();
  @Output() valueChanged = new EventEmitter<EditableTableValueChange>();

  private readonly editingCells = new Set<string>();
  private readonly draftCellValues = new Map<string, string>();
  private readonly cancelPendingCells = new Set<string>();

  constructor(
    private readonly toaster: ToasterService,
    private readonly clipboard: Clipboard,
  ) { }

  // Status helpers

  getStatusLabel(status: string): string {
    const normalized = this.normalizeStatus(status);
    if (!normalized) {
      return '-';
    }

    return this.statusLabels[normalized] ?? this.statusLabels.corrected;
  }

  getStatusClass(status: string): string {
    const normalized = this.normalizeStatus(status);

    if (!normalized) {
      return 'editable-review-table__status';
    }

    if (normalized === 'match') {
      return 'editable-review-table__status editable-review-table__status--match';
    }

    if (normalized === 'corrected') {
      return 'editable-review-table__status editable-review-table__status--corrected';
    }

    if (normalized === 'accepted') {
      return 'editable-review-table__status editable-review-table__status--corrected';
    }

    return 'editable-review-table__status editable-review-table__status--mismatch';
  }

  getStatusIcon(status: string): string | null {
    const normalized = this.normalizeStatus(status);
    if (!normalized) {
      return null;
    }

    if (normalized === 'mismatch') {
      return 'del-warning';
    }

    return 'icon-check';
  }

  private normalizeStatus(status: string): EditableFieldStatus | null {
    const value = status.toLowerCase().trim();
    if (value === 'match') return 'match';
    if (value === 'corrected') return 'corrected';
    if (value === 'accepted') return 'accepted';
    if (value === 'mismatch') return 'mismatch';
    return null;
  }

  // Row / cell helpers

  getRowClass(row: EditableReviewRow): string {
    const classes: string[] = [];
    if (this.normalizeStatus(String(row.status)) === 'mismatch') {
      classes.push('editable-review-table__row--mismatch');
    }
    return classes.join(' ');
  }

  isMismatchValueCell(row: EditableReviewRow, columnKey: string): boolean {
    if (row.status !== 'mismatch') return false;
    return (
      columnKey === 'submittedValue' ||
      columnKey === 'extractedValue' ||
      columnKey === 'finalValue'
    );
  }

  getOptions(column: EditableTableColumn): EditableOverrideReasonOption[] {
    if (column.options?.length) return column.options;
    return this.overrideReasonOptions;
  }

  hasOverrideOptions(column: EditableTableColumn): boolean {
    return this.getOptions(column).length > 0;
  }

  /**
   * "Edited" badge: shown only when the host explicitly marks the row as user-edited.
   * This preserves the latest-branch behaviour (isFinalValueEdited flag on the row)
   * rather than the copy-branch's value-comparison approach.
   */
  isFinalValueEdited(row: EditableReviewRow, column: EditableTableColumn): boolean {
    if (column.key !== 'finalValue') return false;
    return row['isFinalValueEdited'] === true;
  }

  isOverrideReasonMissing(row: EditableReviewRow): boolean {
    return this.invalidOverrideReasonRowKeys.includes(row.key);
  }

  isFinalValueMissing(row: EditableReviewRow): boolean {
    return this.invalidFinalValueRowKeys.includes(row.key);
  }

  /**
   * Override reason is enabled only after the host has flagged the row as user-edited.
   * Preserves the latest-branch behaviour.
   */
  isOverrideReasonEnabled(row: EditableReviewRow): boolean {
    if (this.isReadOnly) return false;
    if (row['isFinalValueEdited'] !== true) return false;

    const finalValue = this.getCellText(row, 'finalValue').trim();
    if (!finalValue || finalValue === '-') return false;

    return true;
  }

  getDropdownOptions(column: EditableTableColumn): DropdownOption[] {
    return this.getOptions(column).map((option) => ({
      value: option.value,
      label: option.label,
    }));
  }

  getCellText(row: EditableReviewRow, columnKey: string): string {
    const value = row[columnKey];
    if (value === null || value === undefined) return '-';
    return String(value);
  }

  getPercentValue(row: EditableReviewRow, columnKey: string): string {
    const value = row[columnKey];
    if (value === null || value === undefined) {
      return '-';
    }

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (!normalized || normalized === '-' || normalized === 'null' || normalized === 'undefined') {
        return '-';
      }
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? `${value}%` : '-';
    }

    const parsed = Number(value);
    if (Number.isFinite(parsed)) return `${parsed}%`;
    return '-';
  }

  trackRow(_index: number, row: EditableReviewRow): string {
    return row.key;
  }

  trackColumn(_index: number, column: EditableTableColumn): string {
    return column.key;
  }

  // Cell editing (latest-branch draft/cancel logic)

  isCellEditing(rowKey: string, columnKey: string): boolean {
    return this.editingCells.has(this.getCellEditKey(rowKey, columnKey));
  }

  startCellEdit(row: EditableReviewRow, column: EditableTableColumn): void {
    if (!column.clickableToEdit || this.isReadOnly) return;

    const cellKey = this.getCellEditKey(row.key, column.key);
    this.editingCells.add(cellKey);
    this.draftCellValues.set(cellKey, this.getCellText(row, column.key));
  }

  stopCellEdit(row: EditableReviewRow, column: EditableTableColumn): void {
    const cellKey = this.getCellEditKey(row.key, column.key);
    this.editingCells.delete(cellKey);
    this.draftCellValues.delete(cellKey);
    this.cancelPendingCells.delete(cellKey);
  }

  getCellDraftValue(row: EditableReviewRow, column: EditableTableColumn): string {
    const cellKey = this.getCellEditKey(row.key, column.key);
    return this.draftCellValues.get(cellKey) ?? this.getCellText(row, column.key);
  }

  onCellDraftChange(row: EditableReviewRow, column: EditableTableColumn, value: string): void {
    const cellKey = this.getCellEditKey(row.key, column.key);
    this.draftCellValues.set(cellKey, value);
  }

  onCellFocusOut(
    row: EditableReviewRow,
    rowIndex: number,
    column: EditableTableColumn,
    event: FocusEvent,
  ): void {
    const currentTarget = event.currentTarget as HTMLElement | null;
    const relatedTarget = event.relatedTarget as Node | null;
    if (currentTarget && relatedTarget && currentTarget.contains(relatedTarget)) {
      return;
    }

    const cellKey = this.getCellEditKey(row.key, column.key);
    if (this.cancelPendingCells.has(cellKey)) {
      this.stopCellEdit(row, column);
      return;
    }

    const draftValue = this.draftCellValues.get(cellKey) ?? this.getCellText(row, column.key);
    if (draftValue !== this.getCellText(row, column.key)) {
      this.onCellValueChange(row, rowIndex, column.key, draftValue);
    }

    this.stopCellEdit(row, column);
  }

  prepareCancelCellEdit(row: EditableReviewRow, column: EditableTableColumn, event: MouseEvent): void {
    event.preventDefault();
    const cellKey = this.getCellEditKey(row.key, column.key);
    this.cancelPendingCells.add(cellKey);
  }

  cancelCellEdit(row: EditableReviewRow, column: EditableTableColumn, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.stopCellEdit(row, column);
  }

  // Copy-to-clipboard

  isCopyEnabledForColumn(column: EditableTableColumn): boolean {
    if (column.key === 'submittedValue') {
      return true;
    }
    return this.enableCopyMode && this.isReadOnly && column.type !== 'select';
  }

  /**
   * Handles clicks on the final-value trigger button.
   * In copy mode → copies the value; otherwise → starts inline edit.
   */
  onFinalValueTriggerClick(row: EditableReviewRow, column: EditableTableColumn, event: Event): void {
    event.preventDefault();

    if (this.isCopyEnabledForColumn(column)) {
      this.copyCellValue(this.getCellText(row, column.key), event);
      return;
    }

    this.startCellEdit(row, column);
  }

  /** Handles clicks on read-only cells (status, percent, default). */
  onReadOnlyCellClick(row: EditableReviewRow, column: EditableTableColumn, event: Event): void {
    event.preventDefault();

    if (!this.isCopyEnabledForColumn(column)) return;

    this.copyCellValue(this.getCopyText(row, column), event);
  }

  private getCopyText(row: EditableReviewRow, column: EditableTableColumn): string {
    if (column.type === 'status') {
      return this.getStatusLabel(this.getCellText(row, column.key));
    }
    if (column.type === 'percent') {
      return this.getPercentValue(row, column.key);
    }
    return this.getCellText(row, column.key);
  }

  private copyCellValue(value: string, event?: Event): void {
    event?.stopPropagation();

    const normalizedValue = value?.trim() ?? '';
    if (!normalizedValue || normalizedValue === '-') return;

    if (!this.clipboard.copy(normalizedValue)) return;

    this.toaster.show({
      message: 'Copied to clipboard',
      status: 'success',
      position: 'top-right',
    });
  }

  // Value change emission

  onFinalValueInput(rowKey: string, value: string): void {
    this.finalValueChanged.emit({ rowKey, value });
  }

  onOverrideReasonSelect(rowKey: string, reason: string): void {
    this.overrideReasonChanged.emit({ rowKey, reason });
  }

  onCellValueChange(row: EditableReviewRow, rowIndex: number, columnKey: string, value: string): void {
    if (this.isReadOnly) return;

    this.valueChanged.emit({ rowKey: row.key, rowIndex, columnKey, value });

    if (columnKey === 'finalValue') {
      this.onFinalValueInput(row.key, value);
      return;
    }

    if (columnKey === 'overrideReason') {
      this.onOverrideReasonSelect(row.key, value);
    }
  }

  // Private utils
  private getCellEditKey(rowKey: string, columnKey: string): string {
    return `${rowKey}::${columnKey}`;
  }
}